/**
 * Leviathan Web - Tax Rate Form Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Create/Edit tax rate form
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  ArrowLeft,
  Save,
  Receipt,
  MapPin,
  Calendar,
  Percent,
  DollarSign,
  Package,
  AlertTriangle,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface TaxRateFormData {
  name: string;
  code: string;
  description: string;
  taxType: string;
  rateType: string;
  rate: string;
  minAmount: string;
  maxAmount: string;
  jurisdiction: string;
  country: string;
  state: string;
  county: string;
  city: string;
  zipCodes: string;
  productCategories: string[];
  effectiveDate: string;
  expirationDate: string;
  isActive: boolean;
  isCompound: boolean;
  notes: string;
}

const TAX_TYPES = [
  { value: 'FEDERAL', label: 'Federal Tax' },
  { value: 'STATE', label: 'State Tax' },
  { value: 'COUNTY', label: 'County Tax' },
  { value: 'CITY', label: 'City Tax' },
  { value: 'LOCAL', label: 'Local/District Tax' },
  { value: 'EXCISE', label: 'Excise Tax' },
  { value: 'SALES', label: 'Sales Tax' },
  { value: 'USE', label: 'Use Tax' },
  { value: 'OTHER', label: 'Other' },
];

const RATE_TYPES = [
  { value: 'PERCENT', label: 'Percentage', icon: '%', description: 'Tax as percentage of sale' },
  { value: 'PER_GALLON', label: 'Per Gallon', icon: '/gal', description: 'Fixed amount per gallon' },
  { value: 'PER_UNIT', label: 'Per Unit', icon: '/unit', description: 'Fixed amount per unit' },
  { value: 'FLAT', label: 'Flat Fee', icon: '$', description: 'Fixed fee per transaction' },
];

const PRODUCT_CATEGORIES = [
  'FUEL',
  'ADDITIVE',
  'LUBRICANT',
  'EQUIPMENT',
  'SERVICE',
  'OTHER',
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

/**
 * Tax Rate Form Page
 */
const TaxRateFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const isEdit = id && id !== 'new';

  const [formData, setFormData] = useState<TaxRateFormData>({
    name: '',
    code: '',
    description: '',
    taxType: 'STATE',
    rateType: 'PERCENT',
    rate: '',
    minAmount: '',
    maxAmount: '',
    jurisdiction: '',
    country: 'USA',
    state: '',
    county: '',
    city: '',
    zipCodes: '',
    productCategories: [],
    effectiveDate: new Date().toISOString().split('T')[0] || '',
    expirationDate: '',
    isActive: true,
    isCompound: false,
    notes: '',
  });

  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isEdit) {
      fetchTaxRate();
    }
  }, [id]);

  const fetchTaxRate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tax/rates/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        const data = await response.json();
        const taxRate = data.taxRate;
        setFormData({
          name: taxRate.name || '',
          code: taxRate.code || '',
          description: taxRate.description || '',
          taxType: taxRate.taxType || 'STATE',
          rateType: taxRate.rateType || 'PERCENT',
          rate: taxRate.rate?.toString() || '',
          minAmount: taxRate.minAmount?.toString() || '',
          maxAmount: taxRate.maxAmount?.toString() || '',
          jurisdiction: taxRate.jurisdiction || '',
          country: taxRate.country || 'USA',
          state: taxRate.state || '',
          county: taxRate.county || '',
          city: taxRate.city || '',
          zipCodes: (taxRate.zipCodes || []).join(', '),
          productCategories: taxRate.productCategories || [],
          effectiveDate: taxRate.effectiveDate?.split('T')[0] || '',
          expirationDate: taxRate.expirationDate?.split('T')[0] || '',
          isActive: taxRate.isActive ?? true,
          isCompound: taxRate.isCompound || false,
          notes: taxRate.notes || '',
        });
      }
    } catch (error) {
      console.error('Failed to fetch tax rate:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear error when field changes
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleCategoryToggle = (category: string) => {
    setFormData((prev) => ({
      ...prev,
      productCategories: prev.productCategories.includes(category)
        ? prev.productCategories.filter((c) => c !== category)
        : [...prev.productCategories, category],
    }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.code.trim()) {
      newErrors.code = 'Code is required';
    }

    if (!formData.rate || parseFloat(formData.rate) < 0) {
      newErrors.rate = 'Valid rate is required';
    }

    if (!formData.effectiveDate) {
      newErrors.effectiveDate = 'Effective date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSaving(true);

    try {
      const payload = {
        name: formData.name,
        code: formData.code,
        description: formData.description || null,
        taxType: formData.taxType,
        rateType: formData.rateType,
        rate: parseFloat(formData.rate),
        minAmount: formData.minAmount ? parseFloat(formData.minAmount) : null,
        maxAmount: formData.maxAmount ? parseFloat(formData.maxAmount) : null,
        jurisdiction: formData.jurisdiction || null,
        country: formData.country || 'USA',
        state: formData.state || null,
        county: formData.county || null,
        city: formData.city || null,
        zipCodes: formData.zipCodes
          ? formData.zipCodes.split(',').map((z) => z.trim()).filter(Boolean)
          : [],
        productCategories: formData.productCategories,
        effectiveDate: formData.effectiveDate,
        expirationDate: formData.expirationDate || null,
        isActive: formData.isActive,
        isCompound: formData.isCompound,
        notes: formData.notes || null,
      };

      const url = isEdit
        ? `${API_URL}/api/tax/rates/${id}`
        : `${API_URL}/api/tax/rates`;

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        navigate('/settings/tax');
      } else {
        const data = await response.json();
        setErrors({ submit: data.message || 'Failed to save tax rate' });
      }
    } catch (error) {
      console.error('Failed to save tax rate:', error);
      setErrors({ submit: 'Failed to save tax rate' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-amber font-mono animate-pulse">Loading Tax Rate...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6">
          <div className="flex items-center space-x-4 mb-2">
            <Link
              to="/settings/tax"
              className="flex items-center space-x-2 text-text-secondary hover:text-amber transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-mono">Back to Tax Rates</span>
            </Link>
          </div>
          <h1
            className="text-3xl font-display font-bold text-steel tracking-wide uppercase"
            style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
          >
            {isEdit ? 'Edit Tax Rate' : 'New Tax Rate'}
          </h1>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {errors.submit && (
          <div className="p-4 bg-error-red/10 border border-error-red/30 rounded flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-error-red" />
            <span className="text-error-red font-mono text-sm">{errors.submit}</span>
          </div>
        )}

        {/* Basic Info */}
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-steel" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-steel" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-steel" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-steel" />

          <div className="bg-panel-dark border border-panel-border p-6">
            <div className="flex items-center space-x-2 mb-4 pb-2 border-b border-panel-border">
              <Receipt className="w-5 h-5 text-steel" />
              <h2 className="font-display text-steel uppercase tracking-wide">Basic Information</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-mono text-text-tertiary uppercase mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 bg-background border ${
                    errors.name ? 'border-error-red' : 'border-panel-border'
                  } text-text-primary font-mono focus:border-amber focus:outline-none`}
                  placeholder="e.g., Texas State Tax"
                />
                {errors.name && (
                  <p className="text-error-red text-xs mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-mono text-text-tertiary uppercase mb-1">
                  Code *
                </label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 bg-background border ${
                    errors.code ? 'border-error-red' : 'border-panel-border'
                  } text-text-primary font-mono focus:border-amber focus:outline-none`}
                  placeholder="e.g., TX-STATE"
                />
                {errors.code && (
                  <p className="text-error-red text-xs mt-1">{errors.code}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-mono text-text-tertiary uppercase mb-1">
                  Description
                </label>
                <input
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-background border border-panel-border text-text-primary font-mono focus:border-amber focus:outline-none"
                  placeholder="Optional description"
                />
              </div>

              <div>
                <label className="block text-xs font-mono text-text-tertiary uppercase mb-1">
                  Tax Type *
                </label>
                <select
                  name="taxType"
                  value={formData.taxType}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-background border border-panel-border text-text-primary font-mono focus:border-amber focus:outline-none"
                >
                  {TAX_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-mono text-text-tertiary uppercase mb-1">
                  Rate Type *
                </label>
                <select
                  name="rateType"
                  value={formData.rateType}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-background border border-panel-border text-text-primary font-mono focus:border-amber focus:outline-none"
                >
                  {RATE_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-text-tertiary mt-1">
                  {RATE_TYPES.find((t) => t.value === formData.rateType)?.description}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Rate Configuration */}
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border p-6">
            <div className="flex items-center space-x-2 mb-4 pb-2 border-b border-panel-border">
              {formData.rateType === 'PERCENT' ? (
                <Percent className="w-5 h-5 text-amber" />
              ) : (
                <DollarSign className="w-5 h-5 text-amber" />
              )}
              <h2 className="font-display text-amber uppercase tracking-wide">Rate Configuration</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-mono text-text-tertiary uppercase mb-1">
                  Rate * {formData.rateType === 'PERCENT' ? '(%)' : '($)'}
                </label>
                <input
                  type="number"
                  name="rate"
                  value={formData.rate}
                  onChange={handleChange}
                  step={formData.rateType === 'PERCENT' ? '0.01' : '0.001'}
                  min="0"
                  className={`w-full px-3 py-2 bg-background border ${
                    errors.rate ? 'border-error-red' : 'border-panel-border'
                  } text-text-primary font-mono focus:border-amber focus:outline-none`}
                  placeholder={formData.rateType === 'PERCENT' ? '6.25' : '0.184'}
                />
                {errors.rate && (
                  <p className="text-error-red text-xs mt-1">{errors.rate}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-mono text-text-tertiary uppercase mb-1">
                  Min Amount ($)
                </label>
                <input
                  type="number"
                  name="minAmount"
                  value={formData.minAmount}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 bg-background border border-panel-border text-text-primary font-mono focus:border-amber focus:outline-none"
                  placeholder="Optional minimum"
                />
              </div>

              <div>
                <label className="block text-xs font-mono text-text-tertiary uppercase mb-1">
                  Max Amount ($)
                </label>
                <input
                  type="number"
                  name="maxAmount"
                  value={formData.maxAmount}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 bg-background border border-panel-border text-text-primary font-mono focus:border-amber focus:outline-none"
                  placeholder="Optional maximum"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="isCompound"
                  checked={formData.isCompound}
                  onChange={handleChange}
                  className="w-4 h-4 bg-background border border-panel-border text-amber focus:ring-amber"
                />
                <span className="text-sm text-text-secondary font-mono">
                  Compound tax (calculate on subtotal + other taxes)
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Jurisdiction */}
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-steel" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-steel" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-steel" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-steel" />

          <div className="bg-panel-dark border border-panel-border p-6">
            <div className="flex items-center space-x-2 mb-4 pb-2 border-b border-panel-border">
              <MapPin className="w-5 h-5 text-steel" />
              <h2 className="font-display text-steel uppercase tracking-wide">Jurisdiction</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-mono text-text-tertiary uppercase mb-1">
                  State
                </label>
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-background border border-panel-border text-text-primary font-mono focus:border-amber focus:outline-none"
                >
                  <option value="">All States</option>
                  {US_STATES.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-mono text-text-tertiary uppercase mb-1">
                  County
                </label>
                <input
                  type="text"
                  name="county"
                  value={formData.county}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-background border border-panel-border text-text-primary font-mono focus:border-amber focus:outline-none"
                  placeholder="Optional"
                />
              </div>

              <div>
                <label className="block text-xs font-mono text-text-tertiary uppercase mb-1">
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-background border border-panel-border text-text-primary font-mono focus:border-amber focus:outline-none"
                  placeholder="Optional"
                />
              </div>

              <div>
                <label className="block text-xs font-mono text-text-tertiary uppercase mb-1">
                  Zip Codes
                </label>
                <input
                  type="text"
                  name="zipCodes"
                  value={formData.zipCodes}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-background border border-panel-border text-text-primary font-mono focus:border-amber focus:outline-none"
                  placeholder="77001, 77002, 77003"
                />
                <p className="text-[10px] text-text-tertiary mt-1">Comma-separated</p>
              </div>
            </div>
          </div>
        </div>

        {/* Applicability */}
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

          <div className="bg-panel-dark border border-panel-border p-6">
            <div className="flex items-center space-x-2 mb-4 pb-2 border-b border-panel-border">
              <Package className="w-5 h-5 text-amber" />
              <h2 className="font-display text-amber uppercase tracking-wide">Applies To</h2>
            </div>

            <p className="text-xs text-text-tertiary mb-3">
              Select product categories this tax applies to. Leave empty to apply to all products.
            </p>

            <div className="flex flex-wrap gap-2">
              {PRODUCT_CATEGORIES.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => handleCategoryToggle(category)}
                  className={`px-3 py-2 font-mono text-xs uppercase transition-colors ${
                    formData.productCategories.includes(category)
                      ? 'bg-amber text-background'
                      : 'bg-panel-border text-text-secondary hover:bg-panel-border/80'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Dates & Status */}
        <div className="relative stat-card">
          <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-steel" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-steel" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-steel" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-steel" />

          <div className="bg-panel-dark border border-panel-border p-6">
            <div className="flex items-center space-x-2 mb-4 pb-2 border-b border-panel-border">
              <Calendar className="w-5 h-5 text-steel" />
              <h2 className="font-display text-steel uppercase tracking-wide">Effective Dates</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-mono text-text-tertiary uppercase mb-1">
                  Effective Date *
                </label>
                <input
                  type="date"
                  name="effectiveDate"
                  value={formData.effectiveDate}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 bg-background border ${
                    errors.effectiveDate ? 'border-error-red' : 'border-panel-border'
                  } text-text-primary font-mono focus:border-amber focus:outline-none`}
                />
                {errors.effectiveDate && (
                  <p className="text-error-red text-xs mt-1">{errors.effectiveDate}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-mono text-text-tertiary uppercase mb-1">
                  Expiration Date
                </label>
                <input
                  type="date"
                  name="expirationDate"
                  value={formData.expirationDate}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-background border border-panel-border text-text-primary font-mono focus:border-amber focus:outline-none"
                />
                <p className="text-[10px] text-text-tertiary mt-1">Leave empty for no expiration</p>
              </div>

              <div className="flex items-center">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    className="w-4 h-4 bg-background border border-panel-border text-amber focus:ring-amber"
                  />
                  <span className="text-sm text-text-secondary font-mono">Active</span>
                </label>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-xs font-mono text-text-tertiary uppercase mb-1">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 bg-background border border-panel-border text-text-primary font-mono focus:border-amber focus:outline-none resize-none"
                placeholder="Internal notes..."
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <Link
            to="/settings/tax"
            className="px-6 py-2 bg-panel-border hover:bg-panel-border/80 text-text-secondary font-mono text-sm uppercase tracking-wider transition-colors"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSaving}
            className="flex items-center space-x-2 px-6 py-2 bg-amber hover:bg-amber/90 text-background font-mono text-sm uppercase tracking-wider transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>{isSaving ? 'Saving...' : isEdit ? 'Update Tax Rate' : 'Create Tax Rate'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default TaxRateFormPage;
